import { useState } from "react";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} SchoolInfo
 * @property {string} name - Name of the school
 * @property {string} email - Email of the school
 * @property {number} licenses_assigned - Total licenses assigned to the school
 * @property {number} licenses_used - Number of licenses used
 * @property {string} image_url - Image of the school
 * @property {string} subdomain - Subdomain of the school
 * @property {string} status - Status of the school
 */

/**
 * @typedef {object} PerformingTeacher
 * @property {string} name - Name of the teacher
 * @property {number} avg_progress - Average progress
 */

/**
 * @typedef {object} SubSchoolDetailStats
 * @property {number} total_teachers - Total number of teachers in the sub-school
 * @property {number} avg_student_passing_score - Average student passing score
 * @property {PerformingTeacher} highest_performing_teacher - Highest performing teacher details
 * @property {PerformingTeacher} lowest_performing_teacher - Lowest performing teacher details
 */

/**
 * @typedef {object} TeacherReportItem
 * @property {number} teacher_id - ID of the teacher
 * @property {string} teacher_name - Name of the teacher
 * @property {string[]} classes - Array of class names
 * @property {number} students_assigned - Number of students assigned
 * @property {number} avg_student_progress - Average student progress
 * @property {number} pass_rate - Pass rate percentage
 * @property {number} lessons_assigned - Number of lessons assigned
 */

/**
 * @typedef {object} TeacherListPaginated
 * @property {TeacherReportItem[]} data - Array of teacher report items
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} SubSchoolDetailReportData
 * @property {SchoolInfo} school - School information
 * @property {SubSchoolDetailStats} stats - Overall statistics for the sub-school
 * @property {TeacherListPaginated} teachers - Paginated list of teachers with stats
 */

/**
 * @typedef {object} MetaType
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * Custom hook to fetch sub-school report by ID with pagination
 */
export const useGetSubSchoolReportById = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [reportData, setReportData] = useState(
        /** @type {SubSchoolDetailReportData | null | undefined} */(undefined)
    );
    const [metaData, setMetaData] = useState(
        /** @type {MetaType | null | undefined} */(undefined)
    );

    /**
     * Fetch sub-school report by ID with pagination
     * @async
     * @param {number|string} schoolId - ID of the sub-school
     * @param {number} [page] - Page number for pagination
     * @param {number} [per_page] - Number of items per page
     * @returns {Promise<void>}
     */
    const getSubSchoolReportById = async (schoolId, page = 1, per_page = ITEMS_PER_PAGE) => {
        if (!schoolId) {
            setError("School ID is required");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            const query = new URLSearchParams();
            query.append("page", page.toString());
            query.append("per_page", per_page.toString());

            const url = `${TENANT_DOMAIN}/organization/admin/reports/sub-school/${schoolId}?${query.toString()}`;
            const response = await axiosInstance.get(url);
            const data = response.data?.data;

            setReportData(data);

            setMetaData({
                current_page: data?.teachers?.current_page,
                per_page: data?.teachers?.per_page,
                total: data?.teachers?.total,
                last_page: data?.teachers?.last_page,
            });
        } catch (err) {
            setReportData(null);
            setMetaData(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, reportData, getSubSchoolReportById, metaData };
};